/**
 * Copyright Zendesk, Inc.
 *
 * Use of this source code is governed under the Apache License, Version 2.0
 * found at http://www.apache.org/licenses/LICENSE-2.0.
 */

import { useId } from '@zendeskgarden/container-utilities';
import { useSelection } from '@zendeskgarden/container-selection';
import React from 'react';
import PropTypes from 'prop-types';

const useTabs = function (_temp) {
  let {
    orientation = 'horizontal',
    idPrefix,
    ...options
  } = _temp === void 0 ? {} : _temp;
  const prefix = useId(idPrefix);
  const PANEL_ID = `${prefix}__panel`;
  const TAB_ID = `${prefix}__tab`;
  const {
    selectedItem,
    focusedItem,
    getContainerProps,
    getItemProps
  } = useSelection({
    direction: orientation,
    defaultSelectedIndex: 0,
    ...options
  });
  const getTabListProps = function (_temp2) {
    let {
      role = 'tablist',
      ...other
    } = _temp2 === void 0 ? {} : _temp2;
    return {
      ...getContainerProps(other),
      role: role === null ? undefined : role,
      'aria-orientation': orientation,
      'data-garden-container-id': 'containers.tabs',
      'data-garden-container-version': '1.0.6'
    };
  };
  const getTabProps = _ref => {
    let {
      role = 'tab',
      index,
      ...other
    } = _ref;
    return {
      ...getItemProps(other),
      role: role === null ? undefined : role,
      id: `${TAB_ID}:${index}`,
      'aria-controls': `${PANEL_ID}:${index}`
    };
  };
  const getTabPanelProps = _ref2 => {
    let {
      role = 'tabpanel',
      index,
      item,
      ...other
    } = _ref2;
    return {
      role: role === null ? undefined : role,
      id: `${PANEL_ID}:${index}`,
      hidden: item !== selectedItem,
      'aria-labelledby': `${TAB_ID}:${index}`,
      ...other
    };
  };
  return {
    selectedItem,
    focusedItem,
    getTabListProps,
    getTabProps,
    getTabPanelProps
  };
};

const TabsContainer = _ref => {
  let {
    children,
    render = children,
    ...options
  } = _ref;
  return React.createElement(React.Fragment, null, render(useTabs(options)));
};
TabsContainer.defaultProps = {
  orientation: 'horizontal'
};
TabsContainer.propTypes = {
  children: PropTypes.func,
  render: PropTypes.func,
  rtl: PropTypes.bool,
  orientation: PropTypes.oneOf(['horizontal', 'vertical']),
  idPrefix: PropTypes.string,
  defaultSelectedIndex: PropTypes.number,
  selectedItem: PropTypes.any,
  onSelect: PropTypes.func
};

export { TabsContainer, useTabs };
